import { useState, useEffect } from 'react';
import { FaImages } from 'react-icons/fa';
import { FiCamera } from 'react-icons/fi';
import { AiOutlineTeam, AiOutlineFolder } from 'react-icons/ai';
import { BiMenu } from 'react-icons/bi';
import { BiLogIn, BiGroup, BiGlobe } from 'react-icons/bi';
import { BsTelegram, BsPinterest } from 'react-icons/bs';
import { MdOutlineDarkMode, MdOutlineLightMode } from 'react-icons/md';
import BookmarkIcon from '../Icons/BookmarkIcon';
import { useTranslation } from 'react-i18next';
import SidebarMenu from './SidebarMenu';
import LoginModal from '../Modals/LoginModal';
import CreateChannelModal from '../Modals/CreateChannelModal';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import LanguageSelect from '../Common/LanguageSelect';
import { MegaphoneIcon } from '@heroicons/react/24/outline';
import { useSelector } from 'react-redux';
import {
    sidebarContainer,
    sidebarButtonBase,
    activeButton,
    inactiveButton,
    languageButton
} from '../../styles/commonStyles';
import { CloudUpload, User } from 'lucide-react';
import { TbBucket } from 'react-icons/tb';
import { FaCrown } from 'react-icons/fa';
import { BiCog } from 'react-icons/bi';
import { Crown } from 'lucide-react';
import getIsAdmin from '../../utils/isAdmin';
import { useCollectionsByUserId } from '../../api/collections';
import { useUserLevel } from '../../hooks/useUserLevel';

// AGR: Базовые вкладки - только галерея и меню
const BASE_TABS = [
    { id: 4, icon: <BiMenu className="text-xl md:text-2xl" />, tooltip: 'sidebar.menu', path: '#' },
    { id: 0, icon: <FaImages className="text-xl md:text-2xl" />, tooltip: 'sidebar.gallery', path: '/gallery' },
    { id: 13, icon: <BiGroup className="text-xl md:text-2xl" />, tooltip: 'sidebar.authors', path: '/authors' },
    { id: 14, icon: <BiGlobe className="text-xl md:text-2xl" />, tooltip: 'sidebar.websites', path: '/sources/websites' },
    { id: 15, icon: <BsTelegram className="text-xl md:text-2xl" />, tooltip: 'sidebar.telegram_sources', path: '/sources/telegram' },
    { id: 16, icon: <BsPinterest className="text-xl md:text-2xl" />, tooltip: 'sidebar.pinterest_sources', path: '/sources/pinterest' },
];

// AGR: Вкладки для авторизованных - только закладки (если доступны по уровню)
const AUTH_TABS = [
    { id: 3, icon: <BookmarkIcon />, tooltip: 'sidebar.favorites', path: '/bookmarks' },
];

// AGR: Полный набор админских вкладок (как было раньше)
const ADMIN_TABS = [
    { id: 1, icon: <MegaphoneIcon className="w-5 h-5" />, tooltip: 'tabs.channels', path: '/mychannels' },
    { id: 5, icon: <AiOutlineFolder className="text-xl md:text-2xl" />, tooltip: 'sidebar.categories', path: '/categories' },
    { id: 7, icon: <TbBucket className="text-xl md:text-2xl" />, tooltip: 'sidebar.sandbox', path: '/sandbox' },
    { id: 2, icon: <AiOutlineTeam className="text-xl md:text-2xl" />, tooltip: 'sidebar.mySubscriptions', path: '/mysubscriptions' },
    { id: 6, icon: <CloudUpload className="text-xl md:text-2xl" />, tooltip: 'sidebar.myChannel', path: '/my-channel' },
    { id: 8, icon: <FaCrown className="text-xl md:text-2xl text-yellow-500" />, tooltip: 'sidebar.moderator', path: '/moderator' },
    { id: 9, icon: <BiCog className="text-xl md:text-2xl" />, tooltip: 'sidebar.cacheManagement', path: '/cache-management' },
    { id: 11, icon: <FaCrown className="text-xl md:text-2xl text-red-500" />, tooltip: 'sidebar.adminPanel', path: '/admin' },
    { id: 12, icon: <Crown className="text-xl md:text-2xl text-red-500" />, tooltip: 'sidebar.levelsManagement', path: '/admin/levels' },
];

function Sidebar({ setActiveTab }) {
    const { t, i18n } = useTranslation();
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
    const user = useSelector(state => state.auth.user);
    const { isTelegramApp, telegramUser } = useSelector((state) => state.telegram);
    const { hasFeature } = useUserLevel();
    
    // Получаем каналы пользователя для поиска автоматического канала
    const { data: userChannels } = useCollectionsByUserId(user?.id);
    const defaultChannel = userChannels?.find(channel => channel.is_default);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [iconSize, setIconSize] = useState(20);
    const [isLoginModalOpen, setLoginModalOpen] = useState(false);
    const [isCreateModalOpen, setCreateModalOpen] = useState(false);
    const location = useLocation();
    const { darkMode, handleThemeChange, isTelegramControlled } = useTheme();
    
    // Отладочная информация
    
    const navigate = useNavigate();


    
    
    // Определяем, авторизован ли пользователь (через обычную авторизацию или Telegram)
    const isUserAuthenticated = isAuthenticated || (isTelegramApp && telegramUser);
    const isAdminStatus = getIsAdmin(user, telegramUser);

    // Создаем вкладку профиля если есть автоматический канал и функция MY_PROFILE доступна
    const profileTab = (defaultChannel && hasFeature('MY_PROFILE')) ? {
        id: 10,
        icon: <User className="w-5 h-5" />,
        tooltip: 'sidebar.myProfile',
        path: `/mychannels/${defaultChannel.id}`
    } : null;

    // Объединяем вкладки в зависимости от уровня доступа
    const tabs = isUserAuthenticated 
        ? (isAdminStatus 
            ? [...BASE_TABS, ...(profileTab ? [profileTab] : []), ...AUTH_TABS, ...ADMIN_TABS] 
            : [...BASE_TABS, ...(profileTab ? [profileTab] : []), ...(hasFeature('BOOKMARKS_USE') ? AUTH_TABS : [])])
        : BASE_TABS;

    return (
        <>
            <div className={sidebarContainer + " hidden md:flex lg:flex xl:flex 2xl:flex"}>
                <div className="flex flex-col items-center">
                    {tabs.map((tab) => {
                        const isActive = tab.path !== '#' && location.pathname.startsWith(tab.path);
                        return (
                            <button
                                key={tab.id}
                                onClick={() => {
                                    if (tab.id === 4) {
                                        setIsMenuOpen((prev) => !prev);
                                    } else {
                                        setActiveTab(tab.id);
                                        navigate(tab.path);
                                    }
                                }}
                                className={`${sidebarButtonBase} ${isActive ? activeButton : inactiveButton}`}
                                aria-label={t(tab.tooltip)}
                            >
                                {tab.id === 3 ? (
                                    <BookmarkIcon size={iconSize} />
                                ) : (
                                    tab.icon
                                )}
                                <span className="sr-only">{t(tab.tooltip)}</span>
                            </button>
                        );
                    })}
                </div>
                <div className="flex flex-col items-center mt-auto">
                    {!isUserAuthenticated && !isTelegramApp && (
                        <button
                            onClick={() => setLoginModalOpen(true)}
                            className="dark:hover:bg-gray-600 hover:bg-blue-600 p-2 bg-blue-700 dark:bg-gray-700 rounded text-blue-500 dark:text-blue-600 hover:text-blue-400 dark:hover:text-blue-400 mb-2 flex items-center justify-center transition-colors"
                            aria-label={t('sidebar.login')}
                        >
                            <BiLogIn className="text-xl md:text-2xl" />
                            <span className="sr-only">{t('sidebar.login')}</span>
                        </button>
                    )}
                    {/* Кнопка смены темы - временно скрыта */}
                    {
                    <button
                        onClick={handleThemeChange}
                        className="p-2 bg-blue-700 dark:bg-gray-700 text-blue-500 dark:text-blue-600 hover:text-blue-400 dark:hover:text-blue-400 mb-4 flex items-center justify-center transition-colors"
                        aria-label={t('sidebar.darkMode')}
                        title={t('sidebar.darkMode')}
                    >
                        {darkMode ? (
                            <MdOutlineLightMode className="text-xl md:text-2xl" />
                        ) : (
                            <MdOutlineDarkMode className="text-xl md:text-2xl" />
                        )}
                        <span className="sr-only">{t('sidebar.darkMode')}</span>
                    </button>
                    /**/}
                    {!isTelegramApp && hasFeature('GALLERY_LANG') && (
                        <LanguageSelect />
                    )}
                </div>
                <SidebarMenu 
                    isOpen={isMenuOpen} 
                    onClose={() => setIsMenuOpen(false)}
                    onLoginClick={() => setLoginModalOpen(true)}
                    onCreateChannelClick={() => setCreateModalOpen(true)}
                    onOpen={() => setIsMenuOpen(true)}
                />
            </div>
            {/* Не отображаем LoginModal внутри Telegram WebApp */}
            {!isTelegramApp && (
                <LoginModal
                    isOpen={isLoginModalOpen}
                    onClose={() => setLoginModalOpen(false)}
                />
            )}
            <CreateChannelModal
                isOpen={isCreateModalOpen}
                onClose={() => setCreateModalOpen(false)}
            />
        </>
    );
}

export default Sidebar;
